/**
 * UFS 同步服务
 *
 * 职责：
 * - 接收 documentDAO 的写操作入队（enqueueCreate/Update/Delete）
 * - debounce 后批量推送到 UFS（flush）
 * - 后台 ETag 条件校验本地缓存（revalidate）
 * - 启动时增量同步远端新增/更新（bootstrap -> incrementalSync）
 *
 * 设计要点：
 * - 所有操作非阻塞，documentDAO 写完 IndexedDB 即返回，UFS 推送在后台进行
 * - 失败自动重试，指数退避，超过 maxRetryAttempts 标记 error
 * - 事件广播供 UI 显示同步状态
 */

import type { FileEntry, UnifiedFileServiceClient } from '@ufs/client'
import { DocumentType } from '@/types'
import { getDB } from '@services/database/connection'
import { linkDAO } from '@services/database/linkDAO'
import type { DocumentRecord, SyncQueueEntry } from '@services/database/schema'
import { generateUUID } from '@utils/index'
import { logger } from '@utils/logger'
import { isBinaryPreviewable, isTextFile } from './fileTypeUtils'
import type {
  DocumentUfsMetadata,
  UfsSyncEvent,
  UfsSyncListener,
} from './types'

const DEFAULT_DEBOUNCE_MS = 2000
const DEFAULT_MAX_CONCURRENCY = 6
const DEFAULT_MAX_RETRY_ATTEMPTS = 5

export interface UfsSyncServiceOptions {
  debounceMs?: number
  maxConcurrency?: number
  maxRetryAttempts?: number
}

export class UfsSyncService {
  private readonly client: UnifiedFileServiceClient
  private readonly debounceMs: number
  private readonly maxConcurrency: number
  private readonly maxRetryAttempts: number

  private readonly listeners = new Set<UfsSyncListener>()
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private readonly inflightRevalidates = new Map<string, Promise<void>>()
  private bootstrapDone = false
  private mdnRootIdPromise: Promise<string> | null = null
  private migrationDone = false

  /**
   * 全局同步互斥锁：保证 flush / incrementalSync / revalidate / bootstrap 串行执行
   * 消除并发竞态（如 flush 上传中途被 incrementalSync 覆盖 content）
   * then(fn, fn) + 吞错保证链不断，单次失败不永久阻塞
   */
  private syncLock: Promise<void> = Promise.resolve()

  private withLock<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.syncLock.then(fn, fn)
    this.syncLock = next.then(
      () => undefined,
      () => undefined,
    )
    return next
  }

  constructor(client: UnifiedFileServiceClient, options?: UfsSyncServiceOptions) {
    this.client = client
    this.debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS
    this.maxConcurrency = options?.maxConcurrency ?? DEFAULT_MAX_CONCURRENCY
    this.maxRetryAttempts = options?.maxRetryAttempts ?? DEFAULT_MAX_RETRY_ATTEMPTS
  }

  // ---------- 事件 ----------

  /** 订阅同步事件，返回取消订阅函数 */
  on(listener: UfsSyncListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(event: UfsSyncEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (err) {
        // 监听器异常不能影响同步主流程
        logger.error('UfsSyncService 监听器异常:', err)
      }
    }
  }

  // ---------- 入队 ----------

  /** 文档创建/更新入队 */
  async enqueueUpsert(doc: DocumentRecord): Promise<void> {
    const db = await getDB()
    const record: DocumentRecord = { ...doc, _syncStatus: 'pending' }
    await db.put('documents', record)

    // 同一 docId 的旧队列条目作废（保留最新一条即可）
    await this.removeExistingQueueEntries(doc.id)

    const entry: SyncQueueEntry = {
      id: generateUUID(),
      operation: doc.isDeleted ? 'delete' : 'update',
      docId: doc.id,
      payload: record,
      enqueuedAt: new Date(),
      attempts: 0,
    }
    await db.put('sync_queue', entry)
    this.scheduleFlush()
  }

  /** 文档删除入队（软删：本地保留 isDeleted=true，UFS 侧也标记） */
  async enqueueDelete(docId: string): Promise<void> {
    const db = await getDB()
    const existing = await db.get('documents', docId)
    if (existing) {
      const softDeleted: DocumentRecord = {
        ...existing,
        isDeleted: true,
        _syncStatus: 'pending',
      }
      await db.put('documents', softDeleted)
    }

    await this.removeExistingQueueEntries(docId)

    const entry: SyncQueueEntry = {
      id: generateUUID(),
      operation: 'delete',
      docId,
      payload: existing ? { ...existing, isDeleted: true, _syncStatus: 'pending' } : undefined,
      enqueuedAt: new Date(),
      attempts: 0,
    }
    await db.put('sync_queue', entry)
    this.scheduleFlush()
  }

  private async removeExistingQueueEntries(docId: string): Promise<void> {
    const db = await getDB()
    const tx = db.transaction('sync_queue', 'readwrite')
    const index = tx.store.index('by-doc-id')
    let cursor = await index.openCursor(docId)
    while (cursor) {
      await cursor.delete()
      cursor = await cursor.continue()
    }
    await tx.done
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      void this.flush().catch(err => {
        logger.error('UfsSyncService flush 异常:', err)
      })
    }, this.debounceMs)
  }

  // ---------- flush：消费队列推送 UFS ----------

  async flush(): Promise<void> {
    return this.withLock(() => this.doFlush())
  }

  private async doFlush(): Promise<void> {
    const db = await getDB()
    const entries = await db.getAllFromIndex('sync_queue', 'by-enqueued-at')
    if (entries.length === 0) return

    this.emit({ type: 'syncing', pendingCount: entries.length, timestamp: new Date() })

    await this.runWithConcurrency(entries, this.maxConcurrency, entry =>
      this.processQueueEntry(entry)
    )

    const remaining = await db.count('sync_queue')
    if (remaining === 0) {
      this.emit({ type: 'synced', pendingCount: 0, timestamp: new Date() })
    } else {
      this.emit({
        type: 'error',
        error: `${remaining} 条同步失败，待重试`,
        pendingCount: remaining,
        timestamp: new Date(),
      })
    }
  }

  private async processQueueEntry(entry: SyncQueueEntry): Promise<void> {
    const db = await getDB()
    try {
      if (entry.operation === 'delete') {
        const fileId = entry.payload?._fileId ?? entry.docId
        // UFS 软删除：标记 isDeleted=true 而非物理删除，便于回收站恢复
        if (entry.payload) {
          await this.uploadDocument(entry.payload, entry.force ?? false)
        } else {
          await this.client.requestVoid('DELETE', `/api/files/${encodeURIComponent(fileId)}`)
        }
      } else {
        const doc = entry.payload
        if (!doc) throw new Error('队列条目缺少 payload')
        const result = await this.uploadDocument(doc, entry.force ?? false)
        // 冲突时 uploadDocument 返回 null，已标记 conflict 并 emit，清队列等用户裁决
        if (!result) {
          await db.delete('sync_queue', entry.id)
          return
        }
      }

      await db.delete('sync_queue', entry.id)

      if (entry.operation !== 'delete' && entry.payload) {
        // 读当前记录合并 _syncStatus，避免覆盖 uploadDocument 刚写入的 _fileId / _remoteUpdatedAt
        const current = await db.get('documents', entry.docId)
        if (current) {
          await db.put('documents', {
            ...current,
            _syncStatus: 'synced',
            _syncError: undefined,
          })
        }
      }
    } catch (error) {
      const attempts = entry.attempts + 1
      const lastError = error instanceof Error ? error.message : String(error)
      logger.warn(`UfsSyncService 同步失败 docId=${entry.docId} attempts=${attempts}:`, lastError)

      await db.put('sync_queue', { ...entry, attempts, lastError })

      if (entry.payload) {
        await db.put('documents', {
          ...entry.payload,
          _syncStatus: attempts >= this.maxRetryAttempts ? 'error' : 'pending',
          _syncError: lastError,
        })
      }

      if (attempts >= this.maxRetryAttempts) {
        this.emit({ type: 'error', docId: entry.docId, error: lastError, timestamp: new Date() })
      } else {
        // 指数退避：下次 flush 在 2^attempts 秒后
        const delay = Math.min(1000 * Math.pow(2, attempts), 60000)
        setTimeout(() => {
          void this.flush().catch(() => {})
        }, delay)
      }
    }
  }

  // ---------- UFS 交互 ----------

  /**
   * 上传文档到 UFS
   * @param doc 文档记录
   * @param force 跳过 If-Match 校验（用于 resolveConflictKeepLocal 强制覆盖远端）
   * @returns FileEntry（成功）| null（冲突，已标记 conflict）
   */
  private async uploadDocument(doc: DocumentRecord, force = false): Promise<FileEntry | null> {
    // plain 文件：用 uploadFileVersion 更新原文件，保留原 filename/mime，不写 metadataJson
    if (doc.source === 'plain') {
      return this.uploadPlainDocument(doc)
    }

    const metadata = this.buildMetadata(doc)
    const contentJson = JSON.stringify(doc.content)
    const contentBlob = new Blob([contentJson], { type: 'application/json' })

    // If-Match 乐观锁：传上次拿到的 etag，服务端不匹配返 412（force 时不传，强制覆盖）
    const ifMatch = force ? undefined : doc._etag

    // native 笔记统一进 UFS 根目录下的 mdn 子目录，避免污染 /desk 根目录视图
    const parentId = await this.ensureMdnRoot()

    let fileEntry: FileEntry
    try {
      fileEntry = await this.client.uploadFile({
        file: contentBlob,
        filename: `${doc.id}.json`,
        contentType: 'application/json',
        metadataJson: metadata,
        tags: doc.tags,
        parentId,
        conflictStrategy: 'overwrite',
        ifMatch,
      })
    } catch (error) {
      // 412 Precondition Failed：远端已被其他端修改，标记 conflict 让用户裁决
      if (error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 412) {
        const db = await getDB()
        const current = await db.get('documents', doc.id)
        if (current) {
          await db.put('documents', {
            ...current,
            _syncStatus: 'conflict',
            _syncError: '远端有更新版本（If-Match 失败）',
          })
        }
        this.emit({
          type: 'conflict',
          docId: doc.id,
          error: '远端有更新版本（If-Match 失败）',
          timestamp: new Date(),
        })
        return null
      }
      throw error
    }

    // 记录 fileId + _etag + _remoteUpdatedAt
    const db = await getDB()
    const current = await db.get('documents', doc.id)
    if (current) {
      const remoteUpdatedAt = new Date(fileEntry.updated_at)
      await db.put('documents', {
        ...current,
        _fileId: fileEntry.id,
        _etag: fileEntry.etag ?? current._etag,
        _remoteUpdatedAt: remoteUpdatedAt,
        _syncError: undefined,
      })
    }

    return fileEntry
  }

  /**
   * 上传 plain 文件：用 uploadFileVersion 更新原文件
   * 保留原 filename/mime/content 格式，不写 metadataJson（保持 plain 性质）
   * uploadFileVersion 不支持 ifMatch，plain 文件跳过写冲突检测
   */
  private async uploadPlainDocument(doc: DocumentRecord): Promise<FileEntry> {
    if (!doc._fileId) throw new Error('plain 文件缺少 _fileId')
    if (!doc.originalFilename) throw new Error('plain 文件缺少 originalFilename')

    const textContent = typeof doc.content === 'string' ? doc.content : ''
    const mimeType = doc.originalMimeType || 'text/plain'
    const blob = new Blob([textContent], { type: mimeType })

    const mutation = await this.client.uploadFileVersion(doc._fileId, {
      file: blob,
      filename: doc.originalFilename,
      contentType: mimeType,
      // 不传 metadataJson，保持 plain 性质
    })

    // 更新本地 _etag + _remoteUpdatedAt
    const db = await getDB()
    const current = await db.get('documents', doc.id)
    if (current) {
      await db.put('documents', {
        ...current,
        _etag: mutation.file.etag ?? current._etag,
        _remoteUpdatedAt: new Date(mutation.file.updated_at),
        _syncStatus: 'synced',
        _syncError: undefined,
      })
    }

    return mutation.file
  }

  /**
   * 升级 plain 文件为 native：用 uploadFileVersion 原地改造
   * 同 fileId，写完整 metadata_json（含 docId），content 改为 JSON.stringify 格式
   * 服务端不更新主文件名（file_version.go SQL 不含 name 字段），但功能正常
   */
  async upgradePlainToNative(doc: DocumentRecord): Promise<FileEntry> {
    if (!doc._fileId) throw new Error('plain 文件缺少 _fileId')
    if (doc.source !== 'native') throw new Error('文档已不是 plain 来源')

    const metadata = this.buildMetadata(doc)
    const contentJson = JSON.stringify(doc.content)
    const blob = new Blob([contentJson], { type: 'application/json' })

    // filename 用原文件名（服务端不改主文件名，只进版本记录）
    const mutation = await this.client.uploadFileVersion(doc._fileId, {
      file: blob,
      filename: doc.originalFilename ?? `${doc.id}.json`,
      contentType: 'application/json',
      metadataJson: metadata,
    })

    // 更新本地 _etag + _remoteUpdatedAt
    const db = await getDB()
    const current = await db.get('documents', doc.id)
    if (current) {
      await db.put('documents', {
        ...current,
        _etag: mutation.file.etag ?? current._etag,
        _remoteUpdatedAt: new Date(mutation.file.updated_at),
        _syncStatus: 'synced',
        _syncError: undefined,
      })
    }

    return mutation.file
  }

  /**
   * 从 UFS 列表查询某 docId 的文件元信息（含 updated_at）
   * 用于写冲突检测时拿远端最新 updatedAt
   */
  private async fetchRemoteMeta(docId: string): Promise<FileEntry | null> {
    try {
      const res = await this.client.listFiles({
        metadataKey: 'docId',
        metadataValue: docId,
        pageSize: 1,
      })
      return res.items[0] ?? null
    } catch {
      return null
    }
  }

  private buildMetadata(doc: DocumentRecord): DocumentUfsMetadata {
    return {
      docId: doc.id,
      title: doc.title,
      type: doc.type,
      parentId: doc.parentId,
      order: doc.order,
      isDeleted: doc.isDeleted ?? false,
      createdAt: toIso(doc.createdAt),
      updatedAt: toIso(doc.updatedAt),
      version: doc.metadata?.version ?? 1,
      size: doc.metadata?.size ?? 0,
      checksum: doc.metadata?.checksum,
      links: doc.links ?? [],
    }
  }

  // ---------- mdn 目录管理 ----------

  /**
   * 确保 UFS 根目录下存在 mdn 目录，返回其 id
   * 缓存进行中的 Promise，并发调用复用、不重复创建；失败清缓存允许重试
   */
  private ensureMdnRoot(): Promise<string> {
    if (this.mdnRootIdPromise) return this.mdnRootIdPromise
    const p = this.doEnsureMdnRoot()
    this.mdnRootIdPromise = p
    p.catch(() => { this.mdnRootIdPromise = null })
    return p
  }

  private async doEnsureMdnRoot(): Promise<string> {
    // 分页查根目录下名为 mdn 的目录
    let page = 1
    while (page <= 100) {
      const res = await this.client.listFiles({
        parentId: null,
        page,
        pageSize: 100,
      })
      const dir = res.items.find(f => f.is_directory && f.name === 'mdn')
      if (dir) return dir.id
      if (res.items.length < 100) break
      page++
    }

    // 不存在则创建；并发竞态时另一端可能刚建好，catch 409 后重查
    try {
      const dir = await this.client.createDirectory({
        name: 'mdn',
        parent_id: null,
        conflict_strategy: 'error',
      })
      return dir.id
    } catch (err) {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 409) {
        const res = await this.client.listFiles({ parentId: null, page: 1, pageSize: 100 })
        const dir = res.items.find(f => f.is_directory && f.name === 'mdn')
        if (dir) return dir.id
      }
      throw err
    }
  }

  /**
   * 迁移根目录下散落的 native 笔记 json 到 mdn 子目录
   * 用 parentId:null + metadataKey:'docId' 精确过滤，不误移用户上传的普通 json
   * 单个 move 失败跳过不阻塞；全部成功才置 migrationDone，失败项下次 bootstrap 重试
   */
  private async migrateRootDocsToMdn(): Promise<void> {
    if (this.migrationDone) return

    const mdnDirId = await this.ensureMdnRoot()

    const toMove: FileEntry[] = []
    let page = 1
    while (page <= 100) {
      const res = await this.client.listFiles({
        parentId: null,
        metadataKey: 'docId',
        page,
        pageSize: 100,
      })
      toMove.push(...res.items)
      if (res.items.length < 100) break
      page++
    }

    if (toMove.length === 0) {
      this.migrationDone = true
      return
    }

    let allSuccess = true
    await this.runWithConcurrency(toMove, this.maxConcurrency, async (file) => {
      try {
        await this.client.requestJson<FileEntry>(
          'POST',
          `/api/files/${encodeURIComponent(file.id)}/move`,
          { body: { parent_id: mdnDirId, conflict_strategy: 'error' } }
        )
      } catch (err) {
        allSuccess = false
        const msg = err instanceof Error ? err.message : String(err)
        logger.warn(`迁移笔记到 mdn 目录失败 fileId=${file.id} name=${file.name}: ${msg}`)
      }
    })

    if (allSuccess) {
      this.migrationDone = true
      logger.info(`迁移完成，共移动 ${toMove.length} 个笔记到 mdn 目录`)
    }
  }

  // ---------- plain 文件同步（懒加载壳记录）----------

  /**
   * 同步单个 plain 文件/目录到本地壳记录
   * 不下载正文（懒加载），仅用 file 元数据建/更新 IndexedDB 记录
   * 所有 plain 文件 + UFS 目录都建壳记录（desk 文件树镜像需完整覆盖）
   */
  private async syncPlainFile(
    file: FileEntry,
    localById: Map<string, DocumentRecord>,
    db: Awaited<ReturnType<typeof getDB>>,
  ): Promise<void> {
    const local = localById.get(file.id)

    // 远端已删除：本地有则物理删除（plain 文件/目录无软删机制）
    if (file.deleted_at != null) {
      if (local) {
        await db.delete('documents', file.id)
      }
      return
    }

    if (!local) {
      // 新 plain 文件/目录：写壳记录
      await db.put('documents', this.buildPlainShell(file))
      return
    }

    // 本地已有：若 etag 变了，更新元数据（含 ufsParentId，覆盖改名/移动后的新归属）
    if (file.etag && local._etag !== file.etag) {
      const mime = file.mime_type ?? ''
      const isDir = file.is_directory ?? false
      await db.put('documents', {
        ...local,
        title: file.name.replace(/\.[^.]+$/, ''),
        originalFilename: file.name,
        originalMimeType: isDir ? (mime || 'inode/directory') : mime,
        isDirectory: isDir,
        ufsParentId: file.parent_id ?? null,
        _etag: file.etag,
        contentLoaded: false,
        _remoteUpdatedAt: new Date(file.updated_at),
      })
      return
    }

    // etag 相同但旧记录缺 ufsParentId（v5 升级前数据）：补齐字段，使其进入 by-ufs-parent-id 索引
    // 不补齐则 desk 子目录列表查不到该记录（IndexedDB 索引不收录 undefined）
    if (local.ufsParentId === undefined) {
      const isDir = file.is_directory ?? false
      await db.put('documents', {
        ...local,
        isDirectory: isDir,
        ufsParentId: file.parent_id ?? null,
      })
    }
  }

  /**
   * 构造 plain 文件壳记录（content=null，懒加载）
   * 同时用于 UFS 目录条目（isDirectory=true，desk 文件树镜像）
   */
  private buildPlainShell(file: FileEntry): DocumentRecord {
    const mime = file.mime_type ?? ''
    const isDir = file.is_directory ?? false
    return {
      id: file.id,
      title: file.name.replace(/\.[^.]+$/, ''),
      type: DocumentType.TEXT,
      content: null,
      metadata: {
        version: 1,
        size: file.size,
        checksum: '',
      },
      tags: file.tags ?? [],
      links: [],
      isDeleted: false,
      createdAt: new Date(file.created_at),
      updatedAt: new Date(file.updated_at),
      source: 'plain',
      originalFilename: file.name,
      originalMimeType: isDir ? (mime || 'inode/directory') : mime,
      contentLoaded: false,
      readOnly: isDir || isBinaryPreviewable(mime, file.name),
      isDirectory: isDir,
      ufsParentId: file.parent_id ?? null,
      _etag: file.etag ?? '',
      _fileId: file.id,
      _syncStatus: 'synced',
      _syncError: undefined,
      _remoteUpdatedAt: new Date(file.updated_at),
    }
  }

  // ---------- revalidate：ETag 条件校验 ----------

  /**
   * 后台校验单个文档是否过期
   * step 5 会接入 If-None-Match；当前先直接下载，靠 ETag 字段做软对比
   */
  async revalidate(docId: string): Promise<void> {
    const existing = this.inflightRevalidates.get(docId)
    if (existing) return existing

    const promise = this.withLock(() => this.doRevalidate(docId)).finally(() => {
      this.inflightRevalidates.delete(docId)
    })
    this.inflightRevalidates.set(docId, promise)
    return promise
  }

  private async doRevalidate(docId: string): Promise<void> {
    const db = await getDB()
    const local = await db.get('documents', docId)
    if (!local) return

    // 本地刚创建/更新还未推送到 UFS 时跳过 revalidate：此时 UFS 必然 404，
    // 没必要发请求校验，等 enqueueUpsert 推送完成后再触发校验
    if (local._syncStatus === 'pending') return

    const fileId = local._fileId ?? docId
    try {
      // 用 If-None-Match 让服务端在内容未变更时返回 304，省去正文传输
      const res = await this.client.downloadFile(fileId, {
        ifNoneMatch: local._etag,
      })

      // 304：服务端确认未变更，保留缓存
      if (res.status === 304) {
        return
      }

      // 200：远端有更新。若本地有未解决的冲突，继续保留冲突标记让用户裁决。
      // pending 状态已在 doRevalidate 入口处 early return，不会走到这里。
      if (local._syncStatus === 'conflict') {
        await db.put('documents', {
          ...local,
          _syncStatus: 'conflict',
          _syncError: '远端有更新版本，本地有未推送修改',
        })
        this.emit({
          type: 'conflict',
          docId,
          error: '远端有更新版本，本地有未推送修改',
          timestamp: new Date(),
        })
        return
      }

      const etag = res.headers.get('etag') ?? ''

      // plain 文件：不调 res.json()（正文非 JSON），只更新 _etag + 标记正文过期
      if (local.source === 'plain') {
        await db.put('documents', {
          ...local,
          _etag: etag,
          _fileId: fileId,
          contentLoaded: false,
          _syncStatus: 'synced',
          _syncError: undefined,
        })
        return
      }

      const content = await res.json()
      await db.put('documents', {
        ...local,
        content,
        _etag: etag,
        _fileId: fileId,
        _syncStatus: 'synced',
        _syncError: undefined,
      })
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      this.emit({ type: 'error', docId, error: msg, timestamp: new Date() })
    }
  }

  // ---------- incrementalSync：拉取远端新增/更新 ----------

  async incrementalSync(): Promise<void> {
    return this.withLock(() => this.doIncrementalSync())
  }

  /**
   * 节流的 incrementalSync：距上次触发不足 minIntervalMs 则跳过
   * 用于 documentDAO.getAll 等高频读操作，避免每次都拉远端
   * 窗口 120s：远端变更感知无需更及时，且 bootstrap 会主动写入本 meta 触发节流
   */
  async maybeIncrementalSync(minIntervalMs = 120000): Promise<void> {
    const db = await getDB()
    const meta = await db.get('sync_meta', 'lastIncrementalSyncAt')
    const last = meta?.value as number | undefined
    if (last && Date.now() - last < minIntervalMs) return
    await db.put('sync_meta', {
      key: 'lastIncrementalSyncAt',
      value: Date.now(),
      updatedAt: new Date(),
    })
    void this.incrementalSync().catch(err => {
      logger.warn('maybeIncrementalSync 失败:', err)
    })
  }

  private async doIncrementalSync(): Promise<void> {
    const db = await getDB()

    // 读上次同步游标，初值为 epoch（首次全量）
    const cursorMeta = await db.get('sync_meta', 'lastSyncCursor')
    const cursor = (cursorMeta?.value as string) ?? '1970-01-01T00:00:00Z'
    const isFirstSync = cursor === '1970-01-01T00:00:00Z'

    // 增量同步：传 updatedSince + includeDeleted 感知更新和删除
    // 首次同步不传 updatedSince（全量），但仍 includeDeleted 以建立完整本地状态
    const ufsFiles = await this.listChangedDocuments(isFirstSync ? undefined : cursor)

    const localDocs = await db.getAll('documents')
    const localById = new Map(localDocs.map(d => [d.id, d]))

    const toFetch: FileEntry[] = []
    let maxUpdatedAt = cursor

    for (const file of ufsFiles) {
      // 游标推进前置：所有文件（含 plain）都参与，避免非 native 文件导致游标停滞
      if (file.updated_at > maxUpdatedAt) {
        maxUpdatedAt = file.updated_at
      }

      const meta = file.metadata_json as unknown as DocumentUfsMetadata | null

      // plain 文件分支：无 docId，按 file.id 建壳记录，不下载正文（懒加载）
      if (!meta?.docId) {
        await this.syncPlainFile(file, localById, db)
        continue
      }

      // tombstone 检查：本地已 permanentDelete 的 docId 不重建
      const tombstone = await db.get('tombstones', meta.docId)
      if (tombstone) continue

      const local = localById.get(meta.docId)
      const isRemoteDeleted = file.deleted_at != null || meta.isDeleted === true

      if (isRemoteDeleted) {
        // 远端已删除：本地有则软删，无则跳过（不重建已删文档）
        if (local && !local.isDeleted) {
          await db.put('documents', {
            ...local,
            isDeleted: true,
            _syncStatus: 'synced',
            _syncError: undefined,
            _remoteUpdatedAt: new Date(file.updated_at),
          })
        }
        continue
      }

      if (!local) {
        toFetch.push(file)
        continue
      }

      // etag 相同：远端未变更（可能是 includeDeleted 带出的），跳过下载正文
      if (file.etag && local._etag && file.etag === local._etag) {
        // 旧记录（v5 升级前）缺 ufsParentId，补齐使其进入 by-ufs-parent-id 索引
        if (local.ufsParentId === undefined) {
          await db.put('documents', {
            ...local,
            ufsParentId: file.parent_id ?? null,
          })
        }
        continue
      }

      // 读冲突保护：本地有未推送修改时，远端更新会覆盖本地编辑，标记冲突
      if (local._syncStatus === 'pending' || local._syncStatus === 'conflict') {
        await db.put('documents', {
          ...local,
          _syncStatus: 'conflict',
          _syncError: '远端有更新版本，本地有未推送修改',
          _remoteUpdatedAt: new Date(file.updated_at),
        })
        this.emit({
          type: 'conflict',
          docId: meta.docId,
          error: '远端有更新版本，本地有未推送修改',
          timestamp: new Date(),
        })
        continue
      }

      toFetch.push(file)
    }

    // 写新游标（用 >= 边界，同时间戳多文件靠 etag 去重防漏拉）
    await db.put('sync_meta', {
      key: 'lastSyncCursor',
      value: maxUpdatedAt,
      updatedAt: new Date(),
    })

    if (toFetch.length === 0) return

    this.emit({ type: 'syncing', pendingCount: toFetch.length, timestamp: new Date() })

    await this.runWithConcurrency(toFetch, this.maxConcurrency, async file => {
      const meta = file.metadata_json as unknown as DocumentUfsMetadata
      try {
        const res = await this.client.downloadFile(file.id)
        const etag = res.headers.get('etag') ?? file.etag ?? ''
        const content = await res.json()

        const local = localById.get(meta.docId)
        const remoteUpdatedAt = new Date(file.updated_at)
        const record: DocumentRecord = {
          id: meta.docId,
          title: meta.title,
          type: meta.type,
          content,
          metadata: {
            version: meta.version,
            size: meta.size,
            checksum: meta.checksum ?? '',
          },
          tags: file.tags ?? [],
          // links 远端优先（LWW），远端无则保留本地
          links: meta.links ?? local?.links ?? [],
          isDeleted: meta.isDeleted,
          parentId: meta.parentId,
          order: meta.order,
          // UFS 文件系统 parent_id（desk 文件树镜像用，与 parentId 逻辑父子语义不同）
          ufsParentId: file.parent_id ?? null,
          createdAt: new Date(meta.createdAt),
          updatedAt: remoteUpdatedAt,
          _etag: etag,
          _fileId: file.id,
          _syncStatus: 'synced',
          _syncError: undefined,
          _remoteUpdatedAt: remoteUpdatedAt,
        }
        await db.put('documents', record)
        // 同步 links store 索引，保持与 documents.links 一致
        await linkDAO.syncOutgoingLinks(meta.docId, record.links)
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error)
        logger.warn(`incrementalSync 拉取失败 docId=${meta.docId}:`, msg)
      }
    })

    this.emit({ type: 'synced', timestamp: new Date() })
  }

  /**
   * 拉取变更文档（updatedSince 游标 + includeDeleted）
   * 首次同步传 undefined cursor -> 全量拉取建游标
   */
  private async listChangedDocuments(cursor?: string): Promise<FileEntry[]> {
    const all: FileEntry[] = []
    let page = 1
    // 安全上限：最多拉 1000 页（10 万条），避免异常情况无限循环
    // 注意 includeDeleted=true 时 total 含已删除文件，可能远大于活跃文件数
    for (let i = 0; i < 1000; i++) {
      const res = await this.client.listFiles({
        page,
        pageSize: 100,
        updatedSince: cursor,
        includeDeleted: true,
      })
      all.push(...res.items)
      if (all.length >= res.total || res.items.length === 0) break
      page++
    }
    return all
  }

  // ---------- bootstrap：启动同步 ----------

  async bootstrap(): Promise<void> {
    if (this.bootstrapDone) return

    return this.withLock(async () => {
      if (this.bootstrapDone) return
      this.bootstrapDone = true

      try {
        // 先迁移根目录散落笔记到 mdn 子目录，再推送本地 pending，最后拉取远端更新
        // 迁移必须在 doFlush 之前：否则 uploadFile(parentId=mdn) 在 mdn 目录找不到同名旧文件会创建新文件，根目录旧文件成孤儿
        await this.migrateRootDocsToMdn()
        await this.doFlush()
        await this.doIncrementalSync()
        // 写真实时间戳，让首屏 getAll 触发的 maybeIncrementalSync 在 120s 内被节流跳过
        // 不写则 lastIncrementalSyncAt 仍为 undefined → 节流失效 → 又触发一次 listFiles
        const db = await getDB()
        await db.put('sync_meta', {
          key: 'lastIncrementalSyncAt',
          value: Date.now(),
          updatedAt: new Date(),
        })
        this.emit({ type: 'bootstrap-done', timestamp: new Date() })
      } catch (error) {
        this.bootstrapDone = false
        const msg = error instanceof Error ? error.message : String(error)
        this.emit({ type: 'error', error: `bootstrap 失败: ${msg}`, timestamp: new Date() })
      }
    })
  }

  // ---------- 冲突解决 ----------

  /**
   * 冲突解决：保留本地版本，强制推送到 UFS（跳过写冲突检测）
   * 用于用户确认本地编辑应覆盖远端的场景
   */
  async resolveConflictKeepLocal(docId: string): Promise<void> {
    const db = await getDB()
    const local = await db.get('documents', docId)
    if (!local) return

    const resolved: DocumentRecord = {
      ...local,
      _syncStatus: 'pending',
      _syncError: undefined,
    }
    await db.put('documents', resolved)

    await this.removeExistingQueueEntries(docId)
    const entry: SyncQueueEntry = {
      id: generateUUID(),
      operation: local.isDeleted ? 'delete' : 'update',
      docId,
      payload: { ...resolved, _syncStatus: 'pending' as const },
      enqueuedAt: new Date(),
      attempts: 0,
      force: true,
    }
    await db.put('sync_queue', entry)
    this.scheduleFlush()
  }

  /**
   * 冲突解决：保留远端版本，拉取覆盖本地
   * 用于用户确认远端编辑应覆盖本地的场景
   */
  async resolveConflictKeepRemote(docId: string): Promise<void> {
    const db = await getDB()
    const local = await db.get('documents', docId)
    if (!local) return

    const fileId = local._fileId ?? docId
    const remoteMeta = await this.fetchRemoteMeta(docId)
    if (!remoteMeta) {
      throw new Error('远端文档不存在，无法保留远端版本')
    }

    const meta = remoteMeta.metadata_json as unknown as DocumentUfsMetadata
    const res = await this.client.downloadFile(fileId)
    const etag = res.headers.get('etag') ?? ''
    const content = await res.json()
    const remoteUpdatedAt = new Date(remoteMeta.updated_at)

    const record: DocumentRecord = {
      id: docId,
      title: meta.title,
      type: meta.type,
      content,
      metadata: {
        version: meta.version,
        size: meta.size,
        checksum: meta.checksum ?? '',
      },
      tags: remoteMeta.tags ?? [],
      // links 远端优先（LWW），远端无则保留本地
      links: meta.links ?? local.links,
      isDeleted: meta.isDeleted,
      parentId: meta.parentId,
      order: meta.order,
      ufsParentId: remoteMeta.parent_id ?? null,
      createdAt: new Date(meta.createdAt),
      updatedAt: remoteUpdatedAt,
      _etag: etag,
      _fileId: fileId,
      _syncStatus: 'synced',
      _syncError: undefined,
      _remoteUpdatedAt: remoteUpdatedAt,
    }
    await db.put('documents', record)
    // 同步 links store 索引，保持与 documents.links 一致
    await linkDAO.syncOutgoingLinks(docId, record.links)

    // 清空该 docId 的队列条目，避免下次 flush 又把本地版本推上去
    await this.removeExistingQueueEntries(docId)
  }

  /**
   * 冲突解决：本地内容复制为新文档（副本），当前文档拉远端覆盖
   * 用于用户希望同时保留本地和远端两个版本的场景
   * @returns 新文档 ID（副本）
   */
  async resolveConflictCreateCopy(docId: string): Promise<string> {
    const db = await getDB()
    const local = await db.get('documents', docId)
    if (!local) throw new Error(`文档不存在: ${docId}`)

    // 1. 本地内容复制为新文档（新 id，标记 pending 等待推送）
    const newDocId = generateUUID()
    const now = new Date()
    const copyRecord: DocumentRecord = {
      ...local,
      id: newDocId,
      title: `${local.title} (副本)`,
      createdAt: now,
      updatedAt: now,
      _etag: undefined,
      _fileId: undefined,
      _syncStatus: 'pending',
      _syncError: undefined,
      _remoteUpdatedAt: undefined,
    }
    await db.put('documents', copyRecord)

    await this.removeExistingQueueEntries(newDocId)
    const entry: SyncQueueEntry = {
      id: generateUUID(),
      operation: 'update',
      docId: newDocId,
      payload: { ...copyRecord, _syncStatus: 'pending' as const },
      enqueuedAt: new Date(),
      attempts: 0,
    }
    await db.put('sync_queue', entry)

    // 2. 当前文档拉远端覆盖（等同于 resolveConflictKeepRemote）
    await this.resolveConflictKeepRemote(docId)

    this.scheduleFlush()
    return newDocId
  }

  // ---------- 工具 ----------

  private async runWithConcurrency<T>(
    items: T[],
    limit: number,
    fn: (item: T) => Promise<void>
  ): Promise<void> {
    const queue = [...items]
    const workers: Promise<void>[] = []
    const workerCount = Math.min(limit, items.length)
    for (let i = 0; i < workerCount; i++) {
      workers.push(
        (async () => {
          while (queue.length > 0) {
            const item = queue.shift()
            if (item === undefined) break
            await fn(item)
          }
        })()
      )
    }
    await Promise.all(workers)
  }
}

function toIso(v: Date | string): string {
  return v instanceof Date ? v.toISOString() : v
}

// ---------- 单例 ----------

let serviceInstance: UfsSyncService | null = null

/**
 * 获取 UfsSyncService 单例
 * 未配置 UFS 时抛错，调用方需先调 isUfsSyncAvailable() 判断
 * 首次创建时自动后台触发 bootstrap（推送本地 pending + 拉取远端更新）
 */
export async function getUfsSyncService(): Promise<UfsSyncService> {
  if (serviceInstance) return serviceInstance

  const { getUfsClient, isUfsConfigured } = await import('./UfsClientFactory')
  if (!isUfsConfigured()) {
    throw new Error('UFS 未配置，无法创建同步服务')
  }
  serviceInstance = new UfsSyncService(getUfsClient())
  // 立即占位 lastIncrementalSyncAt：阻止首屏 getAll 在 bootstrap idle 延迟期间抢先触发 incrementalSync
  // bootstrap 真正跑完后会再写一次真实时间戳覆盖
  void (async () => {
    try {
      const db = await getDB()
      await db.put('sync_meta', {
        key: 'lastIncrementalSyncAt',
        value: Date.now(),
        updatedAt: new Date(),
      })
    } catch {
      // 占位失败无关紧要，不阻塞后续 bootstrap
    }
  })()
  // bootstrap 延迟到空闲时机，避免抢首屏主线程/网络槽位
  // requestIdleCallback timeout 2000 保证最多 2s 后必跑；fallback setTimeout 1500ms
  const scheduleIdle = (cb: () => void): void => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      ;(window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback(cb, { timeout: 2000 })
    } else {
      setTimeout(cb, 1500)
    }
  }
  void scheduleIdle(() => {
    serviceInstance!.bootstrap().catch(err => {
      logger.error('UFS bootstrap 失败:', err)
    })
  })
  return serviceInstance
}

/**
 * 检查同步服务是否可用（UFS 已配置）
 */
export async function isUfsSyncAvailable(): Promise<boolean> {
  const { isUfsConfigured } = await import('./UfsClientFactory')
  return isUfsConfigured()
}
